/**
 * Every column the register list can draw, in one place.
 *
 * The list used to hard-code seven tracks in a `COLS` string, seven heads and
 * seven cells - three lists that had to be counted against each other by hand,
 * and which fused fields that had nothing to do with each other just to save
 * width (the county under the legal form, the headcount under the turnover).
 * Fusing them also meant neither half could be sorted or hidden on its own.
 *
 * One definition per field instead: its track, its head, its cell and its sort
 * key together. The table is then a projection of whichever columns the operator
 * has ticked (`useColumnPrefs`), and a wide selection simply makes the card
 * scroll sideways - the card has done that since `listGrid.ts`, and the floor it
 * scrolls below is derived from the tracks, so a new column brings its own room.
 *
 * `sort` is only set where the API will accept it, which is only where the
 * column is INDEXED on a ~376 000 row mirror (see `LIST_SORTS` in
 * ariregister.routes.ts). A head with no `sort` is drawn as plain text rather
 * than as a button that would 400.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { useT } from "../../../i18n/I18nProvider";
import { formatEurShort, type ArListRow, type ArSort } from "../../../lib/ariregister";
import { Avatar, StatusPill, fmtDate, fmtNum } from "../../ui";
import { BrainBadgeCell } from "./brain";
import { ContactCell } from "./ContactCell";
import { registerTone } from "./status";
import { WebNeedPill } from "./webneed";

/** What a cell needs beyond the row itself. */
export interface ColumnCtx {
  t: ReturnType<typeof useT>;
  /** The live search string - the contact cell decides which of a company's two
   *  contacts to show from it. */
  q: string;
}

export interface ArColumn {
  /** Stable id. Stored in the operator's preferences, so renaming one resets
   *  that column for everybody who had an opinion about it. */
  key: string;
  /** i18n key under `ariregister.col`. */
  label: string;
  /** The grid track. `minmax(0,Nfr)` for anything that truncates - a bare `fr`
   *  track still refuses to go below its content's minimum. */
  track: string;
  /** The API sort key, when this column can be ordered by. */
  sort?: ArSort;
  /** Sorted the other way round on first click. Money, dates and counts read
   *  "biggest first"; names and codes read "A first". */
  descFirst?: boolean;
  /** In the default layout. */
  def?: boolean;
  /** Cannot be unticked - the name is what makes a row a row. */
  locked?: boolean;
  cell: (row: ArListRow, ctx: ColumnCtx) => ReactNode;
}

/** A muted dash - what an empty cell looks like everywhere in this table. */
const dash = <span className="crm-mut text-[12.5px]">-</span>;

/** One line of secondary text, truncated. */
function text(value: string | null, className = "crm-mut crm-trunc text-[12.5px]"): ReactNode {
  if (!value) return dash;
  return (
    <span className={className} title={value}>
      {value}
    </span>
  );
}

/**
 * The columns, in the order they are drawn. Ticking a column on does not move
 * it: the order is the table's, so two operators looking at the same register
 * are looking at the same table with more or fewer columns, not at two layouts.
 */
export const AR_COLUMNS: ArColumn[] = [
  {
    key: "name",
    label: "name",
    track: "minmax(0,2fr)",
    sort: "name",
    def: true,
    locked: true,
    cell: (row) => (
      <span className="flex min-w-0 items-center gap-3">
        <Avatar name={row.name} />
        <span className="min-w-0">
          {/* The row ends in a menu button, so the row cannot be an <a>. The
              name stretches an ::after over the whole row instead, which keeps
              it clickable end to end with the button still on top of it. */}
          <Link to={row.regCode} className="crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full">
            {row.name}
          </Link>
          <span className="crm-prow-s crm-tnum crm-trunc block">{row.regCode}</span>
        </span>
      </span>
    ),
  },
  {
    key: "regCode",
    label: "regCode",
    track: ".8fr",
    sort: "regCode",
    // Off by default: it is already the second line under the name. Its own
    // column exists for the operator who wants to order by it - the code is
    // handed out in sequence, so it is a rough age of the company.
    cell: (row) => <span className="crm-mut crm-tnum text-[12.5px]">{row.regCode}</span>,
  },
  {
    key: "status",
    label: "status",
    track: ".85fr",
    sort: "status",
    def: true,
    cell: (row) =>
      row.status ? <StatusPill tone={registerTone(row.status)}>{row.statusText || row.status}</StatusPill> : dash,
  },
  {
    key: "form",
    label: "form",
    track: "minmax(0,1fr)",
    sort: "legalForm",
    def: true,
    cell: (row) => text(row.legalForm, "crm-trunc block text-[13px]"),
  },
  {
    key: "county",
    label: "county",
    track: "minmax(0,1fr)",
    sort: "county",
    def: true,
    cell: (row) => text(row.county),
  },
  {
    key: "registered",
    label: "registered",
    track: ".85fr",
    sort: "registered",
    descFirst: true,
    def: true,
    cell: (row) => <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(row.registeredAt)}</span>,
  },
  {
    key: "contact",
    label: "contact",
    track: "minmax(0,1.1fr)",
    def: true,
    // No sort: the cell is a website plus whichever of the two contacts fits,
    // which is not one column to order by.
    cell: (row, { q }) => <ContactCell row={row} q={q} />,
  },
  {
    key: "email",
    label: "email",
    track: "minmax(0,1.3fr)",
    cell: (row) =>
      row.email ? (
        <a
          href={`mailto:${row.email}`}
          title={row.email}
          // Above the row-covering overlay, or the link is dead.
          className="crm-trunc relative z-[1] block text-[12.5px] text-[var(--color-primary)] hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.email}
        </a>
      ) : (
        dash
      ),
  },
  {
    key: "vat",
    label: "vat",
    track: ".9fr",
    sort: "vat",
    cell: (row) => <span className="crm-mut crm-tnum crm-trunc text-[12.5px]">{row.vatNumber || "-"}</span>,
  },
  {
    key: "revenue",
    label: "revenue",
    track: ".85fr",
    sort: "revenue",
    descFirst: true,
    cell: (row, { t }) => (
      <span
        className="crm-tnum block text-[13.25px] font-bold"
        title={row.taxYear ? t("ariregister.financial.emtaYear", { year: row.taxYear }) : ""}
      >
        {row.taxRevenueCents != null ? formatEurShort(row.taxRevenueCents) : "-"}
      </span>
    ),
  },
  {
    key: "employees",
    label: "employees",
    track: ".6fr",
    sort: "employees",
    descFirst: true,
    cell: (row, { t }) => (
      <span
        className="crm-tnum text-[12.5px]"
        title={row.taxYear ? t("ariregister.financial.emtaYear", { year: row.taxYear }) : ""}
      >
        {row.taxEmployees != null ? fmtNum(row.taxEmployees) : "-"}
      </span>
    ),
  },
  {
    key: "webNeed",
    label: "webNeed",
    track: ".9fr",
    sort: "webNeed",
    // Descending first, always: "who needs a website most" is the only reason
    // anybody clicks this head.
    descFirst: true,
    def: true,
    cell: (row) => <WebNeedPill score={row.webNeedScore} />,
  },
  {
    key: "debt",
    label: "debt",
    track: ".8fr",
    sort: "debt",
    // "Who owes the most" is the only direction this head is ever clicked in.
    descFirst: true,
    // NOT in the default layout. The table is already wide, and this is a
    // disqualifying signal an operator goes looking for rather than one they
    // need on every row.
    cell: (row, { t }) => {
      // An empty cell, never a zero and never a tick. Null here means "not on
      // the newest EMTA list", which is also what every row says while the
      // arrears import has not run - so this cell can say "owes X" but must
      // never say "owes nothing".
      if (row.debtCents == null) return dash;
      return (
        <span title={row.debtDataAt ? t("ariregister.debt.asOf", { date: fmtDate(row.debtDataAt) }) : undefined}>
          <StatusPill tone="lt" className="crm-tnum">
            {formatEurShort(row.debtCents)}
          </StatusPill>
        </span>
      );
    },
  },
  {
    key: "licences",
    label: "licences",
    track: ".55fr",
    sort: "licences",
    // "Who holds the most permits" - a bus company with 693 route permits is a
    // different kind of company from one with a single trade notice.
    descFirst: true,
    cell: (row, { t }) => {
      // Empty, never a zero. Null here means "no valid licence in the newest MTR
      // file", which is also what every row says while that import has not run.
      if (row.licenceCount == null) return dash;
      return (
        <span
          className="crm-tnum text-[12.5px] font-semibold"
          title={
            row.licenceNewestAt
              ? t("ariregister.licence.newest", { date: fmtDate(row.licenceNewestAt) })
              : undefined
          }
        >
          {fmtNum(row.licenceCount)}
        </span>
      );
    },
  },
  {
    key: "licenceNew",
    label: "licenceNew",
    track: ".7fr",
    sort: "licenceNew",
    // Newest first: a permit granted last month is the whole reason this column
    // exists. Can be a FUTURE date - 90 licences in the file start later.
    descFirst: true,
    cell: (row) => (row.licenceNewestAt ? text(fmtDate(row.licenceNewestAt), "crm-tnum text-[12.5px]") : dash),
  },
  {
    key: "brain",
    label: "brain",
    // Narrow: the cell is one glyph and a count, exactly as it is in the leads
    // table, and the verdict rides in the title rather than as a second mark.
    track: ".5fr",
    // NO `sort`. `LIST_SORTS` only accepts columns indexed on a ~376 000 row
    // mirror, and the brain lives in a different table with no relation to this
    // one - a sortable head here would be a button that 400s.
    cell: (row, { t }) => <BrainBadgeCell brain={row.brain} t={t} />,
  },
  {
    key: "activity",
    label: "activity",
    track: "minmax(0,1.4fr)",
    sort: "activity",
    // Sorted by the EMTAK code, not by its text: the code is the indexed column,
    // and it groups related trades together where the alphabet would not.
    cell: (row) => text(row.emtakText),
  },
  {
    key: "address",
    label: "address",
    track: "minmax(0,1.6fr)",
    cell: (row) => text(row.addressFull),
  },
  {
    key: "capital",
    label: "capital",
    track: ".8fr",
    sort: "capital",
    descFirst: true,
    cell: (row) => (
      <span className="crm-mut crm-tnum text-[12.5px]">
        {row.capitalCents != null ? formatEurShort(row.capitalCents) : "-"}
      </span>
    ),
  },
  {
    key: "reportRevenue",
    label: "reportRevenue",
    track: ".85fr",
    sort: "reportRevenue",
    descFirst: true,
    // The annual report's own turnover, which lags the tax office's by a year or
    // two but is the audited figure. Its year is in the title.
    cell: (row, { t }) => (
      <span
        className="crm-tnum text-[12.5px]"
        title={row.reportYear ? t("ariregister.col.reportYear", { year: row.reportYear }) : ""}
      >
        {row.reportRevenueCents != null ? formatEurShort(row.reportRevenueCents) : "-"}
      </span>
    ),
  },
  {
    key: "profit",
    label: "profit",
    track: ".85fr",
    sort: "profit",
    descFirst: true,
    cell: (row, { t }) => (
      <span
        className={`crm-tnum text-[12.5px] ${row.reportProfitCents != null && row.reportProfitCents < 0 ? "text-red-600" : ""}`}
        title={row.reportYear ? t("ariregister.col.reportYear", { year: row.reportYear }) : ""}
      >
        {row.reportProfitCents != null ? formatEurShort(row.reportProfitCents) : "-"}
      </span>
    ),
  },
];

/**
 * The columns an operator who has never touched the picker sees.
 *
 * Seven, not eleven. The default table used to carry VAT number, turnover,
 * headcount and activity as well, which made a first visit a wall of sixteen
 * figures per row and pushed the card into sideways scrolling before anybody had
 * asked for a single one of them. The four that left are the ones you go looking
 * for on purpose - and they are one tick away in the picker, which remembers.
 *
 * "Default" is not "always": anybody who has already saved a layout keeps it, so
 * this only changes what a fresh browser opens with.
 */
export const DEFAULT_COLUMN_KEYS: string[] = AR_COLUMNS.filter((c) => c.def).map((c) => c.key);

/** Columns that cannot be turned off, so a bad stored preference cannot leave
 *  the table with no way into a company. */
export const LOCKED_COLUMN_KEYS: string[] = AR_COLUMNS.filter((c) => c.locked).map((c) => c.key);

/** The grid tracks for a set of visible columns, plus the row-menu track at the
 *  end and (for an operator who can dial) the call-list checkbox in front. */
export function columnTracks(shown: ArColumn[], withPicks: boolean): string {
  return [withPicks ? "34px" : null, ...shown.map((c) => c.track), "44px"].filter(Boolean).join(" ");
}
